import type { RefObject } from 'react';
import { useDialog } from '@/hooks/useDialog';

interface ReportPrintButtonProps {
  title: string;
  containerRef: RefObject<HTMLDivElement>;
  disabled?: boolean;
  variant?: 'print' | 'pdf';
  label?: string;
  subtitle?: string;
}

export function ReportPrintButton({ title, containerRef, disabled = false, variant = 'print', label, subtitle }: ReportPrintButtonProps) {
  const { showAlert } = useDialog();

  const printReport = () => {
    const source = containerRef.current?.querySelector('table');
    if (!source) return;
    const printWindow = window.open('about:blank', '_blank');
    if (!printWindow) {
      showAlert({ type: 'error', title: 'No se pudo imprimir', message: 'Permití las ventanas emergentes para abrir la impresión.' });
      return;
    }

    const table = source.cloneNode(true) as HTMLTableElement;
    const actionColumns = Array.from(table.querySelectorAll('thead th'))
      .flatMap((header, index) => header.textContent?.trim() === 'Acciones' ? [index] : [])
      .reverse();
    for (const row of Array.from(table.rows)) {
      for (const index of actionColumns) if (row.cells[index]) row.deleteCell(index);
    }
    const doc = printWindow.document;
    // Data is copied as DOM nodes, never interpolated into executable HTML.
    doc.write(`<!doctype html><html lang="es"><head><meta charset="utf-8">
      <style>
        @page { size: A4 landscape; margin: 12mm; }
        body { font: 10pt Arial, sans-serif; color: #111; margin: 20px; }
        h1 { font-size: 15pt; margin: 0 0 8px; }
        p { margin: 0 0 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        th, td { padding: 5px 7px; text-align: left; vertical-align: top; }
        thead { display: table-header-group; }
        th { border-bottom: 1px solid #555; font-weight: 600; }
        tr { break-inside: avoid; }
        .text-right { text-align: right; font-variant-numeric: tabular-nums; }
        .whitespace-nowrap { white-space: nowrap; }
        .ml-2 { margin-left: 6px; }
        .text-xs { font-size: 8pt; }
        button { margin-bottom: 16px; padding: 8px 12px; cursor: pointer; }
        @media print { body { margin: 0; } button { display: none; } }
      </style></head><body></body></html>`);
    doc.close();
    doc.title = title;
    const retry = doc.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Imprimir / Guardar PDF';
    retry.onclick = () => printWindow.print();
    const heading = doc.createElement('h1');
    heading.textContent = title;
    const date = doc.createElement('p');
    date.textContent = subtitle || `Análisis del día ${new Intl.DateTimeFormat('es-UY', { timeZone: 'America/Montevideo' }).format(new Date())}`;
    doc.body.append(retry, heading, date, doc.importNode(table, true));
    printWindow.requestAnimationFrame(() => printWindow.requestAnimationFrame(() => {
      printWindow.focus();
      printWindow.print();
    }));
  };

  // Both actions use this same document so the saved PDF matches the printed report.
  return <button type="button" onClick={printReport} disabled={disabled}
    title={variant === 'pdf' ? 'Seleccione Guardar como PDF en el diálogo de impresión' : undefined}
    className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variant === 'pdf' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-700 hover:bg-blue-800'}`}
  >{label ?? (variant === 'pdf' ? 'Exportar PDF' : 'Imprimir')}</button>;
}
